import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, Check, X, Star } from "lucide-react";
import { toast } from "sonner";
import { api, money } from "../lib/api";

const EMPTY = { name: "", tagline: "", features: [], highlight: false, active: true, prices: { monthly: "", quarterly: "", yearly: "" } };
const INTERVALS = [["monthly", "mo"], ["quarterly", "qtr"], ["yearly", "yr"]];

export default function PackagesView() {
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [featuresText, setFeaturesText] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/master/packages").then(({ data }) => setPackages(data)).catch(() => toast.error("Could not load packages")).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(EMPTY); setFeaturesText(""); setEditing({ isNew: true }); };
  const openEdit = (pkg) => {
    setForm({ ...EMPTY, ...pkg, prices: { monthly: pkg.prices?.monthly ?? "", quarterly: pkg.prices?.quarterly ?? "", yearly: pkg.prices?.yearly ?? "" } });
    setFeaturesText((pkg.features || []).join("\n"));
    setEditing(pkg);
  };
  const close = () => setEditing(null);
  const setPrice = (key, value) => setForm((f) => ({ ...f, prices: { ...f.prices, [key]: value } }));

  const save = async () => {
    if (!form.name.trim()) { toast.error("Package name is required"); return; }
    const prices = {};
    for (const [key] of INTERVALS) prices[key] = form.prices[key] === "" || form.prices[key] == null ? null : Number(form.prices[key]);
    if (!prices.monthly && !prices.quarterly && !prices.yearly) { toast.error("Set a price for at least one interval"); return; }
    setBusy(true);
    const payload = {
      name: form.name.trim(), tagline: form.tagline, features: featuresText.split("\n").map((f) => f.trim()).filter(Boolean),
      highlight: !!form.highlight, active: !!form.active, prices,
    };
    try {
      if (editing?.isNew) await api.post("/master/packages", payload);
      else await api.put(`/master/packages/${editing.id}`, payload);
      toast.success("Package saved");
      close();
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not save package");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (pkg) => {
    if (!window.confirm(`Delete the "${pkg.name}" package? This cannot be undone.`)) return;
    try {
      await api.delete(`/master/packages/${pkg.id}`);
      toast.success("Package deleted");
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Could not delete package");
    }
  };

  return (
    <section className="panel module-panel" data-testid="packages-view">
      <div className="module-toolbar">
        <span className="table-search-hint">{loading ? "Loading…" : `${packages.length} package${packages.length === 1 ? "" : "s"}`} · shown live on your website</span>
        <button className="primary-button" data-testid="add-package-button" onClick={openNew}><Plus size={16} /> Add package</button>
      </div>

      {!loading && packages.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon"><Star size={22} /></div>
          <h3>No subscription packages yet</h3>
          <p>Create your first package to define the plans you offer clients.</p>
        </div>
      ) : (
        <div className="packages-grid">
          {packages.map((pkg) => (
            <div key={pkg.id} className={`package-card ${pkg.highlight ? "highlight" : ""}`} data-testid={`package-card-${pkg.id}`}>
              <div className="package-card-head">
                <div>
                  <h3>{pkg.name}{pkg.highlight && <Star size={14} className="pkg-star" />}</h3>
                </div>
                <span className={`plan-pill ${pkg.active ? "pro" : "basic"}`}>{pkg.active ? "Active" : "Hidden"}</span>
              </div>
              <div className="package-prices">
                {INTERVALS.filter(([key]) => pkg.prices?.[key]).map(([key, short]) => (
                  <span key={key} className="package-price-chip"><strong>{money(pkg.prices[key], "INR")}</strong>/{short}</span>
                ))}
              </div>
              {pkg.tagline && <p className="package-tagline">{pkg.tagline}</p>}
              <ul className="package-features">
                {(pkg.features || []).map((feature, index) => <li key={index}><Check size={13} /> {feature}</li>)}
              </ul>
              <div className="package-actions">
                <button className="secondary-button" data-testid={`edit-package-${pkg.id}`} onClick={() => openEdit(pkg)}><Pencil size={14} /> Edit</button>
                <button className="danger-link" data-testid={`delete-package-${pkg.id}`} onClick={() => remove(pkg)}><Trash2 size={14} /> Delete</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <div className="modal-backdrop" onClick={close}>
          <section className="create-modal" role="dialog" aria-modal="true" data-testid="package-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-heading">
              <div>
                <p className="eyebrow">FLOWSPHERE / {editing.isNew ? "NEW PACKAGE" : "EDIT PACKAGE"}</p>
                <h2>{editing.isNew ? "New subscription package" : "Edit package"}</h2>
                <p>Set prices per billing interval. GST (18%) is added at checkout. Leave an interval blank to hide it.</p>
              </div>
              <button className="icon-button" data-testid="package-modal-close" onClick={close}><X size={18} /></button>
            </div>
            <div className="modal-fields">
              <label className="modal-field wide-field">Package name<input data-testid="package-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Pro" /></label>
              <label className="modal-field">Monthly price (₹)<input data-testid="package-monthly-input" type="number" min="0" value={form.prices.monthly} onChange={(e) => setPrice("monthly", e.target.value)} placeholder="blank = hide" /></label>
              <label className="modal-field">Quarterly price (₹)<input data-testid="package-quarterly-input" type="number" min="0" value={form.prices.quarterly} onChange={(e) => setPrice("quarterly", e.target.value)} placeholder="blank = hide" /></label>
              <label className="modal-field">Yearly price (₹)<input data-testid="package-yearly-input" type="number" min="0" value={form.prices.yearly} onChange={(e) => setPrice("yearly", e.target.value)} placeholder="blank = hide" /></label>
              <label className="modal-field wide-field">Tagline<input data-testid="package-tagline-input" value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value })} placeholder="Short description of this plan" /></label>
              <label className="modal-field wide-field">Features (one per line)<textarea data-testid="package-features-input" rows={6} value={featuresText} onChange={(e) => setFeaturesText(e.target.value)} placeholder={"Unlimited invoices\nInventory management\nPriority support"} /></label>
              <label className="modal-field checkbox-field"><input type="checkbox" data-testid="package-highlight-input" checked={form.highlight} onChange={(e) => setForm({ ...form, highlight: e.target.checked })} /> Highlight as recommended</label>
              <label className="modal-field checkbox-field"><input type="checkbox" data-testid="package-active-input" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> Active / visible on website</label>
            </div>
            <div className="modal-actions">
              <button className="secondary-button" onClick={close}>Cancel</button>
              <button className="primary-button" data-testid="save-package-button" disabled={busy} onClick={save}>{busy ? "Saving…" : "Save package"}</button>
            </div>
          </section>
        </div>
      )}
    </section>
  );
}
